import React, { useState, useEffect, useRef } from 'react';
import { Hourglass, ChevronDown, ChevronUp, Tags, CheckSquare, Square, Menu, Sparkles } from 'lucide-react';
import { noteCategories } from './noteCategories';
import NavDrawer from './NavDrawer';
import AITaskPlannerOverlay from './AITaskPlannerOverlay';
import CategoryContextOverlay from './CategoryContextOverlay';

const NavBar = React.forwardRef(({
  activeFeature,
  onChangeFeature,
  onFilterCategories,
  currentFilters,
  userPreferences,
  user,
  updateUserPreferences,
  onCreateAINote,
}, ref) => {
  const [isCategoryContextOpen, setIsCategoryContextOpen] = useState(false);

  // Expose the function via ref
  React.useImperativeHandle(ref, () => ({
    openCategoryContext: () => setIsCategoryContextOpen(true)
  }));

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(currentFilters);
  const [isMobile, setIsMobile] = useState(false);
  const [isAIOverlayOpen, setIsAIOverlayOpen] = useState(false);
  const filterRef = useRef(null);
  const navbarRef = useRef(null);

  const features = [
    { name: 'Next Hour', icon: <Hourglass size={20} /> },
    { name: 'AI Task Planner', icon: <Sparkles size={20} /> },
  ];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setSelectedCategories(currentFilters);
  }, [currentFilters]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterOpen]);

  const navbarStyle = {
    backgroundImage: `linear-gradient(to right, ${userPreferences.gradientFrom}, ${userPreferences.gradientTo})`,
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev => {
      const newSelection = prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category];
      onFilterCategories(newSelection);
      return newSelection;
    });
  };

  const selectAllCategories = () => {
    const allCategories = noteCategories.map(c => c.name);
    setSelectedCategories(allCategories);
    onFilterCategories(allCategories);
  };

  const deselectAllCategories = () => {
    setSelectedCategories([]);
    onFilterCategories([]);
  };

  const handleSaveCategoryContext = (contexts) => {
    // Save to userPreferences or wherever you store the category contexts
    const updatedPreferences = {
      ...userPreferences,
      categoryContexts: contexts
    };
    updateUserPreferences(updatedPreferences);
  };

  return (
    <>
      <nav className="shadow-md relative z-10" style={navbarStyle} ref={navbarRef}>
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          <div className="relative flex items-center justify-between h-16">
            <div className="absolute left-0 inset-y-0 flex items-center">
              <button
                onClick={() => setIsDrawerOpen(true)}
                className="inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-opacity-10 hover:bg-black focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              >
                <Menu size={24} />
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="flex space-x-4">
                {features.map((feature) => (
                  <button
                    key={feature.name}
                    onClick={() => {
                      if (feature.name === 'AI Task Planner') {
                        setIsAIOverlayOpen(true);
                      } else {
                        onChangeFeature(feature.name);
                      }
                    }}
                    className={`${activeFeature === feature.name
                      ? 'bg-opacity-20 bg-black text-white'
                      : 'text-white hover:bg-opacity-10 hover:bg-black'
                      } px-3 py-2 rounded-md text-sm font-medium flex items-center transition-colors duration-200`}
                    title={feature.name}
                  >
                    {feature.icon}
                    <span className={`ml-2 ${isMobile ? 'hidden' : 'inline'}`}>{feature.name}</span>
                  </button>
                ))}
                <div className="relative" ref={filterRef}>
                  <button
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    className="text-white hover:bg-opacity-10 hover:bg-black px-3 py-2 rounded-md text-sm font-medium flex items-center transition-colors duration-200"
                    title="Categories"
                  >
                    <Tags size={20} />
                    <span className={`ml-2 ${isMobile ? 'hidden' : 'inline'}`}>Categories</span>
                    {isFilterOpen ? (
                      <ChevronUp size={16} className={`${isMobile ? 'ml-0' : 'ml-1'}`} />
                    ) : (
                      <ChevronDown size={16} className={`${isMobile ? 'ml-0' : 'ml-1'}`} />
                    )}
                  </button>
                  {isFilterOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg z-30">
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setIsFilterOpen(false);
                            setIsCategoryContextOpen(true);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M13.4 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7.4" /><path d="M2 6h4" /><path d="M2 10h4" /><path d="M2 14h4" /><path d="M2 18h4" /><path d="M21.378 5.626a1 1 0 1 0-3.004-3.004l-5.01 5.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z" /></svg>
                          Category Context
                        </button>
                        <div className="border-t border-gray-200 my-1"></div>
                        <button
                          onClick={selectAllCategories}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <CheckSquare size={16} className="mr-2" />
                          Select All
                        </button>
                        <button
                          onClick={deselectAllCategories}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Square size={16} className="mr-2" />
                          Deselect All
                        </button>
                        <div className="border-t border-gray-200 my-1"></div>
                        {noteCategories.map((category) => (
                          <button
                            key={category.name}
                            onClick={() => toggleCategory(category.name)}
                            className={`flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${selectedCategories.includes(category.name)
                              ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                              : ''
                              }`}
                          >
                            <span className="mr-2">{category.icon}</span>
                            {category.name}
                            {selectedCategories.includes(category.name) && (
                              <CheckSquare size={16} className="ml-auto" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <NavDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        user={user}
        userPreferences={userPreferences}
        updateUserPreferences={updateUserPreferences}
      />

      <AITaskPlannerOverlay
        isOpen={isAIOverlayOpen}
        onClose={() => setIsAIOverlayOpen(false)}
        onCreateNote={onCreateAINote}
        defaultNoteColor={userPreferences.defaultNoteColor}
        userPreferences={userPreferences}
        updateUserPreferences={updateUserPreferences}
      />

      <CategoryContextOverlay
        isOpen={isCategoryContextOpen}
        onClose={() => setIsCategoryContextOpen(false)}
        initialContexts={userPreferences.categoryContexts || {}}
        onSave={handleSaveCategoryContext}
      />
    </>
  );
});

export default NavBar;